/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Sidebar Component
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/components/Layout/Sidebar.js
import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FiHome,
  FiBook,
  FiBookOpen,
  FiAward,
  FiTrendingUp,
  FiUser,
  FiMonitor
} from 'react-icons/fi';

const SidebarContainer = styled.aside`
  width: 250px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-right: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  
  @media (max-width: 1024px) {
    width: 70px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 1024px) {
    justify-content: center;
    padding: ${({ theme }) => theme.spacing[3]};
  }
  
  span {
    font-size: ${({ theme }) => theme.fonts.sizes.xl};
    font-weight: 700;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
  
  .logo-icon {
  
    font-size: 24px;
    color: ${({ theme }) => theme.colors.primary};
    margin-right: ${({ theme }) => theme.spacing[2]};
    
    @media (max-width: 1024px) {
      margin-right: 0;
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 1024px) {
    justify-content: center;
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.background};
  margin-right: ${({ theme }) => theme.spacing[3]};
  box-shadow: ${({ theme }) => theme.shadows.glow};
  
  @media (max-width: 1024px) {
    margin-right: 0;
  }
`;

const UserDetails = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
  
  h3 {
    font-size: ${({ theme }) => theme.fonts.sizes.md};
    margin-bottom: 2px;
  }
  
  p {
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
    max-width: 170px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.primary : theme.colors.textSecondary};
  border-left: 3px solid ${({ isActive, theme }) =>
    isActive ? theme.colors.primary : 'transparent'};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.cardGlow : 'transparent'};
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.cardGlow};
  }
  
  svg {
    font-size: 20px;
    margin-right: ${({ theme }) => theme.spacing[3]};
    
    @media (max-width: 1024px) {
      margin-right: 0;
    }
  }
  
  span {
    @media (max-width: 1024px) {
      display: none;
    }
  }
  
  @media (max-width: 1024px) {
    justify-content: center;
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const StatsContainer = styled.div`
  margin-top: auto;
  padding: ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  span.label {
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 2px;
  }
  
  span.value {
    font-size: ${({ theme }) => theme.fonts.sizes.xl};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Sidebar = () => {
  const location = useLocation();
  const { currentUser } = useAuth();

  const getInitials = (name) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <SidebarContainer>
      <Logo to="/">
        <img
          src={process.env.PUBLIC_URL + '/images/logo.png'}
          alt="C.H.I.E.F Logo"
          className="logo-icon"
          style={{
            width: 32,
            height: 32,
            marginRight: 12,
            objectFit: 'contain'
          }}
        />
        <span>C.H.I.E.F</span>
      </Logo>

      <UserInfo>
        <Avatar>{getInitials(currentUser?.name || 'U')}</Avatar>
        <UserDetails>
          <h3>{currentUser?.name || 'User'}</h3>
          <p>{currentUser?.email || 'user@thecyberhub.in'}</p>
        </UserDetails>
      </UserInfo>

      <NavMenu>
        <NavItem to="/" isActive={location.pathname === '/'}>
          <FiHome />
          <span>Dashboard</span>
        </NavItem>


        <NavItem to="/vms" isActive={location.pathname.includes('/vms')}>
          <FiMonitor />
          <span>Virtual Machines</span>
        </NavItem>

        <NavItem to="/courses" isActive={location.pathname.includes('/courses')}>
          <FiBook />
          <span>Browse Courses</span>
        </NavItem>

        <NavItem to="/learning" isActive={location.pathname === '/learning'}>
          <FiBookOpen />
          <span>My Learning</span>
        </NavItem>

        {!currentUser?.workshop_code && (
          <NavItem to="/achievements" isActive={location.pathname === '/achievements'}>
            <FiAward />
            <span>Achievements</span>
          </NavItem>
        )}

        <NavItem to="/leaderboard" isActive={location.pathname === '/leaderboard'}>
          <FiTrendingUp />
          <span>Leaderboard</span>
        </NavItem>

        <NavItem to="/profile" isActive={location.pathname === '/profile'}>
          <FiUser />
          <span>My Profile</span>
        </NavItem>
      </NavMenu>

      <StatsContainer>
        <StatItem>
          <span className="label">XP</span>
          <span className="value">{currentUser?.reward_points || 0}</span>
        </StatItem>

        <StatItem>
          <span className="label">Streak</span>
          <span className="value">{currentUser?.current_streak || 1}</span>
        </StatItem>
      </StatsContainer>
    </SidebarContainer>
  );
};

export default Sidebar;